import React, { useEffect, useState } from 'react';
import { Info, HeartHandshake, Tag, Mail, Landmark, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { ASSOC } from '../mock';

const DEFAULTS = {
  overview: 'This is the members portal for the association — a single place to run its affairs online. It brings together role-based member access, a members directory and profiles, an editable page/content system, a shared photo gallery, an online shop, treasurer and banking tools, and a full meeting manager with agendas, anonymous voting, auto-generated minutes and a governance/motions register. Everything is organised around member classes (public, contact, non-financial, financial, committee and administrators) so each person sees exactly what they should.',
  author_quote: 'This system is a work of esprit de corps by a nameless disabled veteran.',
  author_note: 'Built and given freely to the association in the spirit of service — sappers helping sappers.',
  version: 'v1.0.0',
  contact_email: '',
};

export default function About() {
  const { settings, org, isAdmin, getAbout, saveAbout } = useAuth();
  const title = (settings && settings.header && settings.header.title) || (org && org.name) || ASSOC.name;
  const [data, setData] = useState(DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAbout().then((d) => {
      const merged = { ...DEFAULTS };
      Object.keys(DEFAULTS).forEach((k) => { if (d && d[k]) merged[k] = d[k]; });
      setData(merged);
    }).catch(() => {});
  }, [getAbout]);

  const startEdit = () => { setDraft(data); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try { const d = await saveAbout(draft); const merged = { ...DEFAULTS }; Object.keys(DEFAULTS).forEach((k) => { if (d && d[k]) merged[k] = d[k]; }); setData(merged); setEditing(false); toast.success('About page updated.'); }
    catch { toast.error('Could not save.'); }
    setBusy(false);
  };

  const contactEmail = data.contact_email;
  const F = (label, key, textarea) => (
    <label className="block text-xs mb-2" style={{ color: 'var(--olive)' }}>{label}
      {textarea
        ? <textarea value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} rows={4} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid={`about-edit-${key}`} />
        : <input value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} className="w-full mt-1 border rounded px-2 py-1.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid={`about-edit-${key}`} />}
    </label>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="about-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-full" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Info size={22} /></div>
          <div>
            <h1 className="font-title text-3xl sm:text-4xl" style={{ color: 'var(--maroon)' }}>About this app</h1>
            <p className="text-sm" style={{ color: 'var(--olive)' }}>{title}</p>
          </div>
        </div>
        {isAdmin && !editing && (
          <button onClick={startEdit} data-testid="about-edit-btn" className="inline-flex items-center gap-1.5 text-sm font-title px-3 py-1.5 rounded" style={{ border: '1px solid var(--maroon)', color: 'var(--maroon)' }}><Pencil size={14} /> Edit</button>
        )}
      </div>

      {editing ? (
        <div className="bg-white rounded-lg border p-6" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="about-editor">
          {F('Overview', 'overview', true)}
          {F('Author quote', 'author_quote', true)}
          {F('Author note', 'author_note', true)}
          {F('Version', 'version', false)}
          {F('Contact email (optional)', 'contact_email', false)}
          <div className="flex gap-2 mt-2">
            <button onClick={save} disabled={busy} data-testid="about-save-btn" className="inline-flex items-center gap-1.5 text-sm font-title px-4 py-2 rounded text-white" style={{ background: 'var(--maroon)', opacity: busy ? 0.7 : 1 }}><Save size={15} /> Save</button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 text-sm font-title px-3 py-2 rounded" style={{ border: '1px solid rgba(0,0,0,0.2)', color: 'var(--navy)' }}><X size={15} /> Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <section className="bg-white rounded-lg border p-6 mb-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <h2 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>Overview</h2>
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: 'var(--olive)' }}>{data.overview}</p>
          </section>

          <section className="bg-white rounded-lg border p-6 mb-5" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="about-author">
            <h2 className="font-title text-lg mb-2 flex items-center gap-2" style={{ color: 'var(--navy)' }}><HeartHandshake size={18} /> About the author</h2>
            <p className="text-base leading-relaxed italic whitespace-pre-line" style={{ color: 'var(--olive)' }}>&ldquo;{data.author_quote}&rdquo;</p>
            {data.author_note && <p className="text-sm mt-3 whitespace-pre-line" style={{ color: 'var(--olive)' }}>{data.author_note}</p>}
          </section>

          <div className="grid sm:grid-cols-2 gap-5">
            <section className="bg-white rounded-lg border p-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="about-version">
              <h2 className="font-title text-lg mb-2 flex items-center gap-2" style={{ color: 'var(--navy)' }}><Tag size={18} /> Version</h2>
              <p className="text-2xl font-title" style={{ color: 'var(--maroon)' }}>{data.version}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Members portal build</p>
            </section>

            <section className="bg-white rounded-lg border p-6" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="about-contact">
              <h2 className="font-title text-lg mb-2 flex items-center gap-2" style={{ color: 'var(--navy)' }}><Mail size={18} /> Contact the author</h2>
              <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>The author remains anonymous. For anything about this system, please reach the association — they can pass a message on.</p>
              {contactEmail ? (
                <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-2 text-sm font-title px-3 py-2 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="about-contact-email"><Mail size={15} /> {contactEmail}</a>
              ) : (
                <a href="/contact" className="inline-flex items-center gap-2 text-sm font-title px-3 py-2 rounded" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="about-contact-link"><Landmark size={15} /> Contact the association</a>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
